// EnableIPWhitelist.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
    Grid, TextField, MenuItem, Card, Typography, Button,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, TablePagination, Dialog, DialogActions,
    DialogContent, DialogTitle, Collapse, IconButton, Box,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import { Alert } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import Autocomplete from '@mui/material/Autocomplete';
import CustomButton from '../../../src/components/CustomButton/CustomButton';
import Layout from '../../components/Wrappers/Layout';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import SOLogout from '../../components/Logout/SOLogout';
import regenerateToken from '../../components/RegenerateToken/regenerateToken';
import { getCustomerList } from '../../redux/actions/getCustomerListAction';
import { getCustomerIdAndNameList } from '../../redux/actions/getCustomerIdAndNameListAction';
import { updateIPwhitelistAction } from '../../redux/actions/updateIPwhitelistAction';
import success from '../../assets/img/success.png';
import failed from '../../assets/img/failed.png';

const useStyles = makeStyles({ table: { minWidth: 650 } });

const EnableIPWhitelist = () => {
    // Add this ref at the top with other state declarations
    const prevUpdateStatusRef = useRef('');
    const hasMounted = useRef(false);
    const classes = useStyles();
    const dispatch = useDispatch();

    // ── Search state ──────────────────────────────────────────────────────────
    const [merchantId, setMerchantId] = useState('');
    const [merchantName, setMerchantName] = useState('');

    // ── Table / pagination ────────────────────────────────────────────────────
    const [customerListData, setCustomerListData] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    // ── Edit modal state ──────────────────────────────────────────────────────
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editCustomerId, setEditCustomerId] = useState('');
    const [isIpWhitelistFlag, setIsIpWhitelistFlag] = useState('');
    const [isRedirectFlag, setIsRedirectFlag] = useState('');
    const [isRegenerateLinkFlag, setIsRegenerateLinkFlag] = useState('');
    const [redirectUrl, setRedirectUrl] = useState('');
    const [notificationUrl, setNotificationUrl] = useState('');
    const[landingpageUrl,setLandingpageUrl] = useState('');
    const [expandedRows, setExpandedRows] = useState({});

    // ── Response dialog ───────────────────────────────────────────────────────
    const [respDialogOpen, setRespDialogOpen] = useState(false);
    const [respStatus, setRespStatus] = useState('');
    const [respMessage, setRespMessage] = useState('');

    // ── Redux selectors ───────────────────────────────────────────────────────
    const regenerateTokenRes = useSelector(({ regenerateToken }) => regenerateToken);
    const getCustomerListRes = useSelector(({ getCustomerList }) => getCustomerList);
    const getCustomerIdAndNameListRes = useSelector(({ getCustomerIdAndNameList }) => getCustomerIdAndNameList);
    const updateIPwhitelistRes = useSelector(({ updateIPwhitelist }) => updateIPwhitelist);
    console.log("updateIPwhitelistRes", updateIPwhitelistRes)

    const authToken = regenerateTokenRes?.data?.AuthToken;
    const walletOwnerId = regenerateTokenRes?.data?.partnerId || '';

    const [copiedField, setCopiedField] = useState('');

    const handleCopyUrl = (url, fieldKey) => {
        if (!url || url === '-') return;
        navigator.clipboard.writeText(url).then(() => {
            setCopiedField(fieldKey);
            setTimeout(() => setCopiedField(''), 1500);
        });
    };

    // ── Autocomplete filter (min 3 chars) ─────────────────────────────────────
    const filterOptionsL = (options, { inputValue }) => {
        if (inputValue.length < 3) return [];
        return options.filter(o => o.includes(inputValue)).slice(0, 20);
    };

    const toggleRow = (customerId) => {
        setExpandedRows((prev) => ({ ...prev, [customerId]: !prev[customerId] }));
    };

    // ── Load customer ID/name list on mount ───────────────────────────────────
    useEffect(() => {
        if (regenerateTokenRes?.data?.status === 'success') {
            dispatch(getCustomerIdAndNameList(
                {
                    loginDetails: { walletOwnerId },
                    actionCustomerMasterRequestVO: { customerNameOrId: '', role: 'Merchant' },

                },
                authToken
            ));
        }
    }, [regenerateTokenRes?.data?.status]);

    // ── Search ────────────────────────────────────────────────────────────────
    const searchFunction = () => {
        setPage(0);
        setIsLoading(true);
        setHasSearched(true);

        // Extract only the ID part before " - " if autocomplete gives "ID - Name"
        const cleanMerchantId = merchantId.split('-')[0].trim();

        dispatch(getCustomerList(
            {
                authentication: { partnerId: walletOwnerId },
                customerRequestVo: {
                    customerID: cleanMerchantId || '',
                    customerName: merchantName || '',
                    userRole: '',
                },
            },
            authToken
        ));
    };

    // ── getCustomerList response ──────────────────────────────────────────────
    useEffect(() => {
        if (!getCustomerListRes?.data?.status) return;
        const { status, description, customerMasterDetailsRequestVOList } = getCustomerListRes.data;

        if (status === 'Success') {
            setCustomerListData([].concat(customerMasterDetailsRequestVOList || []));
        }
        if (status === 'Failed') {
            if (description === 'NOT FOUND') setCustomerListData([]);
            if (description === 'session out') SOLogout(dispatch, authToken, walletOwnerId);
            if (description === 'Authorization failed') regenerateToken(dispatch, authToken, walletOwnerId);
        }
        setIsLoading(false);
    }, [getCustomerListRes?.data?.status]);

    // ── updateIPwhitelist response ────────────────────────────────────────────
    // useEffect(() => {
    //     if (!updateIPwhitelistRes?.data?.status) return;
    //     const { status, description } = updateIPwhitelistRes.data;

    //     setRespStatus(status);
    //     setRespMessage(description);
    //     setRespDialogOpen(true);

    //     if (status === 'success') {
    //         setEditModalOpen(false);
    //         searchFunction(); // refresh table
    //     }
    //     if (status === 'failed') {
    //         if (description === 'session out') SOLogout(dispatch, authToken, walletOwnerId);
    //         if (description === 'Authorization failed') regenerateToken(dispatch, authToken, walletOwnerId);
    //     }
    // }, [updateIPwhitelistRes?.data?.status]);

    // useEffect(() => {
    //     if (!updateIPwhitelistRes?.data?.status) return;

    //     const { status, description } = updateIPwhitelistRes.data;

    //     // ✅ Only fire if this is a NEW response (not the same stale status)
    //     if (prevUpdateStatusRef.current === status && respDialogOpen) return;
    //     prevUpdateStatusRef.current = status;

    //     setRespStatus(status);
    //     setRespMessage(description);
    //     setRespDialogOpen(true);

    //     if (status === 'success') {
    //         setEditModalOpen(false);
    //         searchFunction();
    //     }
    //     if (status === 'failed') {
    //         if (description === 'session out') SOLogout(dispatch, authToken, walletOwnerId);
    //         if (description === 'Authorization failed') regenerateToken(dispatch, authToken, walletOwnerId);
    //     }
    // }, [updateIPwhitelistRes?.data]);  // ✅ Watch entire data object, not just status


    // Replace the updateIPwhitelist useEffect
    useEffect(() => {
        // ✅ Skip on first mount — stale Redux data from previous session
        if (!hasMounted.current) {
            hasMounted.current = true;
            return;
        }

        if (!updateIPwhitelistRes?.data?.status) return;

        const { status, description } = updateIPwhitelistRes.data;

        if (prevUpdateStatusRef.current === status && respDialogOpen) return;
        prevUpdateStatusRef.current = status;

        setRespStatus(status);
        setRespMessage(description);
        setRespDialogOpen(true);

        if (status === 'success') {
            setEditModalOpen(false);
            searchFunction();
        }
        if (status === 'failed') {
            if (description === 'session out') SOLogout(dispatch, authToken, walletOwnerId);
            if (description === 'Authorization failed') regenerateToken(dispatch, authToken, walletOwnerId);
        }
    }, [updateIPwhitelistRes?.data]);

    // ── Pagination ────────────────────────────────────────────────────────────
    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    // ── Open edit modal ───────────────────────────────────────────────────────
    const openEditModal = (row) => {
        console.log("ROW DATA:", row); // ← check exact field names here
        setEditCustomerId(row.customerId);
        setIsIpWhitelistFlag(row.isIpWhitelist || '');
        setIsRedirectFlag(row.isRedirect || '');
        setIsRegenerateLinkFlag(row.isRegenerateLink || '');
        setRedirectUrl(row.redirectUrl || '');
        setNotificationUrl(row.notificationUrl || '');
        setLandingpageUrl(row.landingPageUrl || '');
        setEditModalOpen(true);
    };

    // ── Save update ───────────────────────────────────────────────────────────
    const handleUpdateFlag = () => {
        prevUpdateStatusRef.current = '';
        dispatch(updateIPwhitelistAction(
            {
                actionCustomerMasterRequestVO: {
                    walletOwnerId,
                    customerId: editCustomerId,
                    isIpWhitelist: isIpWhitelistFlag,
                    isRedirect: isRedirectFlag,
                    isRegenerateLink: isRegenerateLinkFlag,
                    redirectUrl,
                    notificationUrl,
                    landingPageUrl:landingpageUrl,
                },
            },
            authToken
        ));
    };


    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            <Layout>
                {/* ── Search Card ─────────────────────────────────────────── */}
                <Card>
                    <Grid
                        container
                        spacing={2}
                        alignItems="flex-end"
                        style={{ padding: '25px', marginTop: '2rem' }}
                    >
                        {/* Merchant ID — Autocomplete */}
                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <Autocomplete
                                options={[].concat(getCustomerIdAndNameListRes?.data?.currencyList || [])}
                                getOptionLabel={(option) => option}
                                filterOptions={filterOptionsL}
                                value={merchantId}
                                onChange={(_, value) => setMerchantId(value || '')}
                                onInputChange={(_, newInputValue) => setMerchantId(newInputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Merchant ID"
                                        variant="standard"
                                        fullWidth
                                        margin="dense"
                                        sx={{
                                            '& .MuiInput-underline:before': { borderBottomColor: 'gray' },
                                            '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'gray' },
                                            '& .MuiInput-underline:after': { borderBottomColor: 'gray' },
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Merchant Name */}
                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <TextField
                                margin="dense"
                                label="Merchant Name"
                                variant="standard"
                                fullWidth
                                value={merchantName}
                                onChange={(e) => setMerchantName(e.target.value)}
                                InputProps={{ sx: { '&:before': { borderBottomColor: 'gray' }, '&:after': { borderBottomColor: 'gray' } } }}
                                InputLabelProps={{ sx: { color: 'gray' } }}
                            />
                        </Grid>

                        {/* Search button */}
                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <CustomButton style={{ padding: '8px' }} onClick={searchFunction}>
                                Search <SearchIcon />
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Card>

                {/* ── Results Card ─────────────────────────────────────────── */}
                {isLoading ? (
                    <LoadingSpinner />
                ) : (
                    <Card style={{ marginTop: '10px' }}>
                        <Grid container style={{ padding: '10px' }}>
                            <Grid item xs={12}>
                                <Typography variant="body1" style={{ fontWeight: '700' }} className="themeText">
                                    Merchant Configuration
                                </Typography>
                            </Grid>

                            {hasSearched && customerListData.length === 0 ? (
                                <Grid item xs={12}>
                                    <Alert className="my-3 tableHeaderColor" style={{ color: 'white' }} variant="secondary">
                                        No Records Found!
                                    </Alert>
                                </Grid>
                            ) : (
                                <TableContainer component={Paper} className="my-3">
                                    <Table className={classes.table} size="small" aria-label="enable ip whitelist table">
                                        <TableHead>
                                            <TableRow className="tableHeaderColor">
                                                {['', 'Customer Id', 'Name', 'IP Whitelist Flag', 'Is Redirect', 'Is Regenerate Link', 'Action'].map((h) => (
                                                    <TableCell key={h} align="center" style={{ fontWeight: 'bold', color: 'white' }}>
                                                        {h}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {[]
                                                .concat(customerListData)
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((row) => (
                                                    <React.Fragment key={row.customerId}>
                                                        <TableRow>
                                                            <TableCell align="center">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => toggleRow(row.customerId)}
                                                                >
                                                                    {expandedRows[row.customerId]
                                                                        ? <KeyboardArrowUpIcon />
                                                                        : <KeyboardArrowDownIcon />}
                                                                </IconButton>
                                                            </TableCell>
                                                            <TableCell align="center" className="themeColor" style={{ fontWeight: '600' }}>
                                                                {row.customerId || '-'}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {row.customerName || '-'}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {['Y', 'N'].includes(row.isIpWhitelist) ? row.isIpWhitelist : '-'}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {['Y', 'N'].includes(row.isRedirect) ? row.isRedirect : '-'}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {['Y', 'N'].includes(row.isRegenerateLink) ? row.isRegenerateLink : '-'}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <CustomButton style={{ padding: '8px' }} onClick={() => openEditModal(row)}>
                                                                    <EditIcon style={{ color: '#fff', fontSize: '18px' }} />
                                                                    &nbsp;Edit
                                                                </CustomButton>
                                                            </TableCell>
                                                        </TableRow>

                                                        <TableRow>
                                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                                                                <Collapse in={!!expandedRows[row.customerId]} timeout="auto" unmountOnExit>
                                                                    <Box sx={{ margin: '12px 16px', padding: '12px 16px', backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: '6px' }}>
                                                                        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: '8px' }}>
                                                                            URL Details
                                                                        </Typography>
                                                                        <Grid container spacing={1} alignItems="center">
                                                                            <Grid item xs={12} sm={2}>
                                                                                <Typography variant="body2" >
                                                                                    Redirect URL:
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={12} sm={10} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                                                                                <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
                                                                                    {row.redirectUrl || '-'}
                                                                                </Typography>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => handleCopyUrl(row.redirectUrl, `redirect-${row.customerId}`)}
                                                                                    disabled={!row.redirectUrl}
                                                                                >
                                                                                    <ContentCopyIcon fontSize="small" />
                                                                                </IconButton>
                                                                                {copiedField === `redirect-${row.customerId}` && (
                                                                                    <Typography variant="caption" style={{ color: 'green', fontWeight: 600 }}>
                                                                                        Copied!
                                                                                    </Typography>
                                                                                )}
                                                                            </Grid>

                                                                            <Grid item xs={12} sm={2}>
                                                                                <Typography variant="body2" >
                                                                                    Notification URL:
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={12} sm={10} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                                                                                <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
                                                                                    {row.notificationUrl || '-'}
                                                                                </Typography>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => handleCopyUrl(row.notificationUrl, `notification-${row.customerId}`)}
                                                                                    disabled={!row.notificationUrl}
                                                                                >
                                                                                    <ContentCopyIcon fontSize="small" />
                                                                                </IconButton>
                                                                                {copiedField === `notification-${row.customerId}` && (
                                                                                    <Typography variant="caption" style={{ color: 'green', fontWeight: 600 }}>
                                                                                        Copied!
                                                                                    </Typography>
                                                                                )}
                                                                            </Grid>

                                                                            <Grid item xs={12} sm={2}>
                                                                                <Typography variant="body2">
                                                                                    Landing Page URL:
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={12} sm={10} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                                                                                <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
                                                                                    {row.landingPageUrl || '-'}
                                                                                </Typography>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => handleCopyUrl(row.landingPageUrl, `landing-${row.customerId}`)}
                                                                                    disabled={!row.landingPageUrl}
                                                                                >
                                                                                    <ContentCopyIcon fontSize="small" />
                                                                                </IconButton>
                                                                                {copiedField === `landing-${row.customerId}` && (
                                                                                    <Typography variant="caption" style={{ color: 'green', fontWeight: 600 }}>
                                                                                        Copied!
                                                                                    </Typography>
                                                                                )}
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Box>
                                                                </Collapse>
                                                            </TableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                ))}
                                        </TableBody>
                                    </Table>

                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25]}
                                        component="div"
                                        count={customerListData.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                </TableContainer>
                            )}
                        </Grid>
                    </Card>
                )}

                {/* ── Edit Modal ───────────────────────────────────────────── */}
                <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)}>
                    <DialogTitle>Edit Merchant Configuration</DialogTitle>
                    <DialogContent style={{ width: '500px' }}>
                        {/* Read-only Customer ID */}
                        <TextField
                            margin="dense"
                            label="Customer ID"
                            variant="standard"
                            fullWidth
                            value={editCustomerId}
                            disabled
                            InputLabelProps={{ sx: { color: 'gray' } }}
                        />

                        {/* IS IP Whitelist Flag */}
                        <TextField
                            select
                            margin="dense"
                            label="IP Whitelist Flag"
                            variant="standard"
                            fullWidth
                            value={isIpWhitelistFlag}
                            onChange={(e) => setIsIpWhitelistFlag(e.target.value)}
                            InputProps={{ sx: { '&:before': { borderBottomColor: 'gray' }, '&:after': { borderBottomColor: 'gray' } } }}
                            InputLabelProps={{ sx: { color: 'gray' } }}
                        >
                            <MenuItem value="Y">Y</MenuItem>
                            <MenuItem value="N">N</MenuItem>
                        </TextField>

                        {/* Is Redirect Flag */}
                        <TextField
                            select
                            margin="dense"
                            label="Is Redirect"
                            variant="standard"
                            fullWidth
                            value={isRedirectFlag}
                            onChange={(e) => setIsRedirectFlag(e.target.value)}
                            InputProps={{ sx: { '&:before': { borderBottomColor: 'gray' }, '&:after': { borderBottomColor: 'gray' } } }}
                            InputLabelProps={{ sx: { color: 'gray' } }}
                        >
                            <MenuItem value="Y">Y</MenuItem>
                            <MenuItem value="N">N</MenuItem>
                        </TextField>

                        {/* Is Regenerate Link Flag */}
                        <TextField
                            select
                            margin="dense"
                            label="Is Regenerate Link"
                            variant="standard"
                            fullWidth
                            value={isRegenerateLinkFlag}
                            onChange={(e) => setIsRegenerateLinkFlag(e.target.value)}
                            InputProps={{ sx: { '&:before': { borderBottomColor: 'gray' }, '&:after': { borderBottomColor: 'gray' } } }}
                            InputLabelProps={{ sx: { color: 'gray' } }}
                        >
                            <MenuItem value="Y">Y</MenuItem>
                            <MenuItem value="N">N</MenuItem>
                        </TextField>
                        {/* Redirect URL */}
                        <TextField
                            margin="dense"
                            label="Redirect URL"
                            variant="standard"
                            fullWidth
                            value={redirectUrl}
                            onChange={(e) => setRedirectUrl(e.target.value)}
                            InputProps={{ sx: { '&:before': { borderBottomColor: 'gray' }, '&:after': { borderBottomColor: 'gray' } } }}
                            InputLabelProps={{ sx: { color: 'gray' } }}
                        />

                        {/* Notification URL */}
                        <TextField
                            margin="dense"
                            label="Notification URL"
                            variant="standard"
                            fullWidth
                            value={notificationUrl}
                            onChange={(e) => setNotificationUrl(e.target.value)}
                            InputProps={{ sx: { '&:before': { borderBottomColor: 'gray' }, '&:after': { borderBottomColor: 'gray' } } }}
                            InputLabelProps={{ sx: { color: 'gray' } }}
                        />
                        <TextField
                            margin="dense"
                            label="Landing Page URL"
                            variant="standard"
                            fullWidth
                            value={landingpageUrl}
                            onChange={(e) => setNotificationUrl(e.target.value)}
                            InputProps={{ sx: { '&:before': { borderBottomColor: 'gray' }, '&:after': { borderBottomColor: 'gray' } } }}
                            InputLabelProps={{ sx: { color: 'gray' } }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <CustomButton style={{ padding: '8px' }} onClick={() => setEditModalOpen(false)}>
                            Cancel
                        </CustomButton>
                        <CustomButton style={{ padding: '8px' }} onClick={handleUpdateFlag}>
                            Save
                        </CustomButton>
                    </DialogActions>
                </Dialog>
            </Layout>

            {/* ── Success Response Dialog ──────────────────────────────────── */}
            {respStatus === 'success' && (
                <Dialog open={respDialogOpen} onClose={() => setRespDialogOpen(false)}>
                    <DialogContent className="text-center" style={{ backgroundColor: '#00b9f51c' }}>
                        <img src={success} alt="success" width="100px" className="my-2" />
                    </DialogContent>
                    <DialogContent>
                        <h4 style={{ margin: '0', textAlign: 'center', fontSize: '15px' }}>{respMessage}</h4>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: 'center' }}>
                        <Button style={{ margin: '10px 0', color: 'white' }} variant="contained" onClick={() => { setRespDialogOpen(false); setRespStatus(''); }}>
                            Ok
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* ── Failed Response Dialog ───────────────────────────────────── */}
            {respStatus === 'failed' && (
                <Dialog open={respDialogOpen} onClose={() => setRespDialogOpen(false)}>
                    <DialogContent className="text-center" style={{ backgroundColor: '#00b9f51c' }}>
                        <img src={failed} alt="failed" width="100px" className="my-2" />
                    </DialogContent>
                    <DialogContent>
                        <h4 style={{ margin: '0', textAlign: 'center', fontSize: '15px' }}>{respMessage}</h4>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: 'center' }}>
                        <Button style={{ margin: '10px 0', color: 'white' }}
                            variant="contained"
                            onClick={() => { setRespDialogOpen(false); setRespStatus(''); }}  >
                            Ok
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

export default EnableIPWhitelist;